"use client";

import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import type { AuthProviderConfigurationStatus } from "@/domain/auth/AuthProviderConfiguration";
import {
	type AuthSessionKernel,
	type AuthSessionState,
	loadAuthSessionKernel,
} from "@/domain/auth/AuthSessionBoundary";
import {
	type AuthoritativeProviderSession,
	AWAITING_AUTH_SESSION_STATE,
	createProviderProductSession,
	nextProviderScopeEpoch,
	type PersistedProductSession,
	PRODUCT_SCOPE_WATERMARK_STORAGE_KEY,
	PRODUCT_SESSION_STORAGE_KEY,
	type ProductScopeBinding,
	parsePersistedProductSession,
	productScopeBinding,
} from "@/domain/auth/ProductSession";
import { artistSupabase } from "@/infrastructure/supabase/client";

export type ProductSessionPhase = "loading" | "guest" | "user" | "error";

export interface ArtistProductSession {
	readonly phase: ProductSessionPhase;
	readonly providerAvailable: boolean;
	readonly providerConfiguration: AuthProviderConfigurationStatus;
	readonly session: PersistedProductSession | null;
	readonly scope: ProductScopeBinding | null;
	readonly error: string | null;
	readonly beginProviderSession: (
		session: AuthoritativeProviderSession,
	) => Promise<boolean>;
	readonly signOut: () => Promise<boolean>;
	readonly retryRestore: () => void;
}

const defaultSession: ArtistProductSession = {
	phase: "guest",
	providerAvailable: false,
	providerConfiguration: "missing",
	session: null,
	scope: null,
	error: null,
	beginProviderSession: async () => false,
	signOut: async () => false,
	retryRestore: () => undefined,
};

const ArtistSessionContext = createContext<ArtistProductSession>(defaultSession);

export function productSessionEventKind(
	event: AuthChangeEvent,
	session: Session | null,
): "signed-in" | "signed-out" | null {
	// getSession() is the authoritative bootstrap above. Supabase immediately
	// replays INITIAL_SESSION to new listeners, so processing it twice would
	// turn a valid guest bootstrap into a rejected signed-in event.
	if (event === "INITIAL_SESSION") return null;
	if (event === "SIGNED_OUT") return "signed-out";
	return session === null ? null : "signed-in";
}

function providerSession(session: Session): AuthoritativeProviderSession {
	const metadata = session.user.user_metadata;
	const accountLabel =
		session.user.email ??
		session.user.phone ??
		(typeof metadata.email === "string" ? metadata.email : null) ??
		(typeof metadata.preferred_username === "string"
			? metadata.preferred_username
			: null) ??
		(typeof metadata.name === "string" ? metadata.name : null) ??
		"Artist";
	return {
		userId: session.user.id.toLowerCase(),
		email: accountLabel,
		expiresAtEpochSeconds:
			session.expires_at ?? Math.floor(Date.now() / 1_000) + 3_600,
	};
}

function productSessionForProvider(
	value: AuthoritativeProviderSession,
): PersistedProductSession {
	const persisted = parsePersistedProductSession(
		window.localStorage.getItem(PRODUCT_SESSION_STORAGE_KEY),
	);
	const scopeEpoch =
		persisted?.mode === "provider" && persisted.ownerUserId === value.userId
			? persisted.scopeEpoch
			: nextProviderScopeEpoch(
					window.localStorage.getItem(PRODUCT_SCOPE_WATERMARK_STORAGE_KEY),
				);
	const productSession = createProviderProductSession(value, scopeEpoch);
	window.localStorage.setItem(
		PRODUCT_SCOPE_WATERMARK_STORAGE_KEY,
		String(scopeEpoch),
	);
	window.localStorage.setItem(
		PRODUCT_SESSION_STORAGE_KEY,
		JSON.stringify(productSession),
	);
	return productSession;
}

export function ArtistSessionProvider({
	children,
	providerConfiguration,
}: Readonly<{
	children: ReactNode;
	providerConfiguration: AuthProviderConfigurationStatus;
}>) {
	const [phase, setPhase] = useState<ProductSessionPhase>("loading");
	const [session, setSession] = useState<PersistedProductSession | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [restoreGeneration, setRestoreGeneration] = useState(0);
	const kernelRef = useRef<AuthSessionKernel | null>(null);
	const reducerStateRef = useRef<AuthSessionState>(AWAITING_AUTH_SESSION_STATE);

	const commitProviderSession = useCallback(
		(
			supabaseSession: Session | null,
			kind: "initial-session" | "signed-in" | "signed-out",
		): boolean => {
			const kernel = kernelRef.current;
			if (kernel === null) return false;
			const now = Math.floor(Date.now() / 1_000);
			const nextProductSession =
				supabaseSession === null
					? null
					: productSessionForProvider(providerSession(supabaseSession));
			const result = kernel.reduce(reducerStateRef.current, {
				nowEpochSeconds: now,
				event: {
					kind,
					session:
						nextProductSession === null
							? null
							: {
									userId: nextProductSession.ownerUserId,
									expiresAtEpochSeconds:
										nextProductSession.expiresAtEpochSeconds,
								},
				},
			});
			if (result.outcome.kind !== "success") return false;
			reducerStateRef.current = result.finalState;
			if (nextProductSession === null) {
				window.localStorage.removeItem(PRODUCT_SESSION_STORAGE_KEY);
			}
			setSession(nextProductSession);
			setPhase(nextProductSession === null ? "guest" : "user");
			setError(null);
			return true;
		},
		[],
	);

	useEffect(() => {
		void restoreGeneration;
		let active = true;
		let unsubscribe: (() => void) | null = null;
		setPhase("loading");
		setError(null);

		void (async () => {
			const kernel = await loadAuthSessionKernel();
			if (!active) return;
			kernelRef.current = kernel;
			reducerStateRef.current = AWAITING_AUTH_SESSION_STATE;

			if (providerConfiguration !== "available") {
				window.localStorage.removeItem(PRODUCT_SESSION_STORAGE_KEY);
				commitProviderSession(null, "initial-session");
				return;
			}

			const supabase = artistSupabase();
			const { data, error: sessionError } = await supabase.auth.getSession();
			if (sessionError) throw sessionError;
			if (!active) return;
			if (!commitProviderSession(data.session, "initial-session")) {
				throw new Error("initial provider session was rejected");
			}

			const listener = supabase.auth.onAuthStateChange((event, nextSession) => {
				queueMicrotask(() => {
					if (!active) return;
					const kind = productSessionEventKind(event, nextSession);
					if (kind === null) return;
					if (!commitProviderSession(nextSession, kind)) {
						setPhase("error");
						setError("认证会话未通过用户作用域契约。");
					}
				});
			});
			unsubscribe = () => listener.data.subscription.unsubscribe();
		})().catch(() => {
			if (!active) return;
			kernelRef.current = null;
			setSession(null);
			setPhase("error");
			setError("Supabase 会话或本地用户作用域规则无法恢复。");
		});

		return () => {
			active = false;
			unsubscribe?.();
		};
	}, [commitProviderSession, providerConfiguration, restoreGeneration]);

	const beginProviderSession = useCallback(
		async (expected: AuthoritativeProviderSession): Promise<boolean> => {
			if (providerConfiguration !== "available") return false;
			const { data, error: sessionError } = await artistSupabase().auth.getSession();
			if (sessionError || data.session?.user.id.toLowerCase() !== expected.userId) {
				return false;
			}
			return commitProviderSession(data.session, "signed-in");
		},
		[commitProviderSession, providerConfiguration],
	);

	const signOut = useCallback(async (): Promise<boolean> => {
		if (providerConfiguration !== "available") return false;
		setPhase("loading");
		setError(null);
		const { error: signOutError } = await artistSupabase().auth.signOut({
			scope: "local",
		});
		if (signOutError || !commitProviderSession(null, "signed-out")) {
			setPhase("error");
			setError("退出未能完成本地作用域关闭，私有能力保持锁定。");
			return false;
		}
		return true;
	}, [commitProviderSession, providerConfiguration]);

	const retryRestore = useCallback(() => {
		setRestoreGeneration((value) => value + 1);
	}, []);

	const value = useMemo<ArtistProductSession>(
		() => ({
			phase,
			providerAvailable: providerConfiguration === "available",
			providerConfiguration,
			session,
			scope: session === null ? null : productScopeBinding(session),
			error,
			beginProviderSession,
			signOut,
			retryRestore,
		}),
		[
			phase,
			providerConfiguration,
			session,
			error,
			beginProviderSession,
			signOut,
			retryRestore,
		],
	);

	return (
		<ArtistSessionContext.Provider value={value}>
			{children}
		</ArtistSessionContext.Provider>
	);
}

export function useArtistSession(): ArtistProductSession {
	return useContext(ArtistSessionContext);
}
